import { ComponentProps, ReactNode, useMemo } from 'react';
import clsx from 'clsx';

import styles from './EventCard.module.css';

interface EventCardProps extends Omit<ComponentProps<'article'>, 'title'> {
  image: ReactNode;
  title: ReactNode;
  description?: ReactNode;
  location?: ReactNode;
  locationLabel?: ReactNode;
  startDate?: string;
  endDate?: string;
  dateLabel?: ReactNode;
  cta?: ReactNode;
  locale?: string;
}

const shortDateFormat = { month: 'numeric', day: 'numeric' } as const;
const shortTimeFormat = { hour: 'numeric', minute: 'numeric' } as const;

export default function EventCard({
  image,
  title,
  description,
  location,
  locationLabel,
  startDate,
  endDate,
  dateLabel,
  cta,
  className,
  locale = 'de-DE',
  ...props
}: EventCardProps) {
  const { start, end, dateValue } = useMemo(() => {
    const fDate = (date?: Date) => date?.toLocaleDateString(locale, shortDateFormat);
    const fTime = (date?: Date) => date?.toLocaleTimeString(locale, shortTimeFormat);

    const [start, end] = [startDate && new Date(startDate), endDate && new Date(endDate)]
      .filter(Boolean)
      .sort((a, b) => (a && b ? a.getTime() - b.getTime() : 0)) as Date[];

    const startIso = start?.toISOString();
    const endIso = end?.toISOString();

    if (!startIso && !endIso) {
      return { start: undefined, end: undefined, dateValue: undefined };
    }

    if (!end || startIso === endIso) {
      return { start: startIso, end: undefined, dateValue: fDate(start) };
    }

    if (startIso && startIso.split('T')[0] === endIso.split('T')[0]) {
      return { start: startIso, end: endIso, dateValue: `${fDate(start)} ${fTime(start)}–${fTime(end)}` };
    }

    return { start: startIso, end: endIso, dateValue: `${fDate(start)}–${fDate(end)}` };
  }, [startDate, endDate, locale]);

  return (
    <article
      className={clsx(styles.card, className)}
      itemScope
      itemType="https://schema.org/Event"
      {...props}
    >
      <div className={styles.image}>{image}</div>

      <div className={styles.header}>
        <h3
          className={styles.title}
          itemProp="name"
        >
          {title}
        </h3>
        {description ? (
          <div
            className={styles.description}
            itemProp="description"
          >
            {description}
          </div>
        ) : null}
      </div>

      <div className={styles.meta}>
        {location ? (
          <p
            className={styles.metaRow}
            itemProp="location"
            itemScope
            itemType="https://schema.org/Place"
          >
            {locationLabel ? <span className={styles.metaLabel}>{locationLabel} </span> : null}
            <span
              className={styles.metaValue}
              itemProp="name"
            >
              {location}
            </span>
          </p>
        ) : null}

        {start ? (
          <p className={styles.metaRow}>
            {dateLabel ? <span className={styles.metaLabel}>{dateLabel} </span> : null}
            <meta
              itemProp="startDate"
              content={start}
            />
            {end && (
              <meta
                itemProp="endDate"
                content={end}
              />
            )}
            <span className={styles.metaValue}>{dateValue}</span>
          </p>
        ) : null}

        {cta ? (
          <div
            className={styles.cta}
            itemProp="offers"
            itemScope
            itemType="https://schema.org/Offer"
          >
            {cta}
          </div>
        ) : null}
      </div>
    </article>
  );
}
